<script>
import bem from '@/utils/bem';
import CreateNewUser from '@/components/modals/Create-new-user.vue';
import ChangeUserPassword from '@/components/modals/Change-user-password.vue';
import { CheckOutlined, EditOutlined } from '@ant-design/icons-vue';
import { mapActions, mapState, mapGetters } from 'vuex';
import constants from '@/constants';
import createDebounce from '@/utils/debounce';
import pageUrls from '@/router/pageUrls';
import columns from './columns';

const {
  roles,
} = constants;

const { usersPage } = pageUrls;

const b = bem('users-page');
export default {
  props: {
    companyId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      limit: 10,
      offset: 0,
      editableData: {},
      roles,
      searchValue: '',
      debounce: createDebounce(),
      selectedField: '',
      usersPage,
      companiesList: [],
    };
  },
  components: {
    CheckOutlined,
    EditOutlined,
    CreateNewUser,
    ChangeUserPassword,
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getUsersAction: 'users/getUsers',
      updateUserAction: 'users/updateUser',
      resetPasswordAction: 'users/resetPassword',
      deleteUserAction: 'users/deleteUser',
      searchUsers: 'users/searchUsers',
      getCompaniesAction: 'companies/getCompanies',
    }),
    setPagination(page, pageSize) {
      this.offset = page * 10;
      this.limit = pageSize;
    },
    getUsers() {
      this.getUsersAction({ limit: this.limit, offset: this.offset, companyId: this.companyId });
    },
    async getCompanies() {
      await this.getCompaniesAction({ limit: 1000, offset: 0 });
    },
    edit(value, fieldType) {
      this.editableData = value;
      this.selectedField = fieldType;
    },
    save(value) {
      this.updateUserAction({
        user: {
          firstName: value.firstName,
          lastName: value.lastName,
          role: value.role,
          companyId: value.companyId,
          isDeleted: value.isDeleted,
          id: value.id,
        },
        limit: this.limit,
        offset: this.offset,
        companyId: this.companyId,
      });
      this.editableData = {};
      this.selectedField = '';
    },
    getRole(value) {
      return roles.find((item) => item.value === value).label;
    },
    getCompanyName(companyId, company) {
      if (!companyId || companyId === null) {
        return 'No company';
      }
      if (company && company.displayName) {
        return company.displayName;
      }
      const found = this.companiesList.find((item) => item.value === companyId);
      return found ? found.label : 'Unknown';
    },
    resetPassword(email) {
      this.resetPasswordAction({ email });
    },
    deleteUser(id) {
      this.deleteUserAction({
        id,
        limit: this.limit,
        offset: this.offset,
        companyId: this.companyId,
      });
    },
    onSearch(e) {
      const { target: { value } } = e;
      this.searchValue = value;
      if (value) {
        this.debounce(() => {
          this.searchUsers({
            limit: this.limit,
            offset: this.offset,
            searchField: e.target.value.trim().toLowerCase(),
          });
        });
      }
    },
    onReset() {
      this.searchValue = '';
      this.getUsers();
    },
  },
  mounted() {
    this.getUsers();
    if (!this.isCompanyUser) {
      this.getCompanies();
    }
  },
  computed: {
    ...mapState({
      users: (state) => state.users.users,
      total: (state) => state.users.total,
      companies: (state) => state.companies.companies,
    }),
    ...mapGetters({
      isCompanyUser: 'auth/isCompanyUser',
    }),
    companiesOptions() {
      return [
        { value: null, label: 'No company' },
        ...this.companies.map((company) => ({
          value: company.id,
          label: company.displayName,
        })),
      ];
    },
    tableColumns() {
      const cols = columns();
      if (this.companyId) {
        return cols.filter((c) => c.key !== 'companyId');
      }
      return cols;
    },
    availableRoles() {
      if (this.companyId) {
        return this.roles.filter((role) => [12, 13].includes(role.value));
      }
      return this.roles;
    },
  },
  watch: {
    offset() {
      this.getUsers();
    },
    limit() {
      this.getUsers();
    },
    companies(newVal) {
      if (newVal && newVal.length > 0) {
        this.companiesList = this.companiesOptions;
      }
    },
    companyId() {
      this.getUsers();
    },
  },
};
</script>

<template>
  <div :class="b('')">
    <div :class="b('top-buttons')">
      <div :class="b('search-button')">
        <a-input-search
            v-model:value="searchValue"
            placeholder="...search"
            size="large"
            @change="onSearch"
        >
          <template #enterButton>
            <a-button @click="onReset"  >Reset</a-button>
          </template>
        </a-input-search>
      </div>
      <CreateNewUser v-if="!companyId" @user-created="getUsers" />
    </div>
    <div :class="b('table')">
      <a-table
          :dataSource="users"
          :columns="tableColumns"
          :pagination="{ pageSize: limit, total, onChange: setPagination }"
      >
        <template #bodyCell="{ column, text, record }">
          <template v-if="column.dataIndex === 'firstName'">
            <div >
              <div v-if="
                editableData.id
                && editableData.id === record.id
                && selectedField === 'firstName'
              ">
                <a-input v-model:value="editableData.firstName" @pressEnter="save(record)" />
                <check-outlined @click="save(record)" />
              </div>
              <div v-else >
                {{ text || ' ' }}
                <edit-outlined @click="edit(record, 'firstName')" />
              </div>
            </div>
          </template>
          <template v-if="column.dataIndex === 'lastName'">
            <div >
              <div v-if="
                editableData.id
                && editableData.id === record.id
                && selectedField === 'lastName'
              ">
                <a-input v-model:value="editableData.firstName" @pressEnter="save(record)" />
                <check-outlined @click="save(record)" />
              </div>
              <div v-else >
                {{ text || ' ' }}
                <edit-outlined @click="edit(record, 'lastName')" />
              </div>
            </div>
          </template>
          <template v-if="column.dataIndex === 'role'">
            <div >
              <div v-if="
                editableData.id
                && editableData.id === record.id
                && selectedField === 'role'
              ">
                <a-select
                    v-model:value="editableData.role"
                    placeholder="Select role"
                    style="width: 150px"
                    :options="availableRoles"
                ></a-select>
                <CheckOutlined @click="save(record)" />
              </div>
              <div v-else >
                {{ getRole(text) || ' ' }}
                <EditOutlined v-if="!isCompanyUser" @click="edit(record, 'role')" />
              </div>
            </div>
          </template>
          <template v-if="column.dataIndex === 'companyId'">
            <div >
              <div v-if="
                editableData.id
                && editableData.id === record.id
                && selectedField === 'companyId'
              ">
                <a-select
                    v-model:value="editableData.companyId"
                    placeholder="Select company"
                    style="width: 200px"
                    :options="companiesOptions"
                ></a-select>
                <CheckOutlined @click="save(record)" />
              </div>
              <div v-else >
                {{ getCompanyName(record.companyId, record.company) }}
                <EditOutlined v-if="!isCompanyUser" @click="edit(record, 'companyId')" />
              </div>
            </div>
          </template>
          <template v-if="column.dataIndex === 'isDeleted'">
            <div >
              <div v-if="
                editableData.id
                && editableData.id === record.id
                && selectedField === 'isDeleted'
              ">
                <a-select
                    v-model:value="editableData.isDeleted"
                    placeholder="Select status"
                    style="width: 150px"
                    :options="[
                      { value: false, label: 'Active' },
                      { value: true, label: 'Deleted' }
                    ]"
                ></a-select>
                <CheckOutlined @click="save(record)" />
              </div>
              <div v-else >
                <span :style="{ color: record.isDeleted ? 'red' : 'green', fontWeight: 'bold' }">
                  {{ record.isDeleted ? 'Deleted' : 'Active' }}
                </span>
                <EditOutlined @click="edit(record, 'isDeleted')" />
              </div>
            </div>
          </template>
          <template v-else-if="column.dataIndex === 'id'">
            <div :class="b('buttons')">
              <div :class="b('button')">
                <a-button
                    type="primary"
                    html-type="button"
                    @click="$router.push(`${usersPage}/${record.id}`)"
                >
                  Edit addresses
                </a-button>
              </div>
              <div :class="b('button')">
                <a-button
                    type="primary"
                    html-type="button"
                    @click="resetPassword(record.email)"
                >
                  Reset password
                </a-button>
              </div>
              <div :class="b('button')">
                <ChangeUserPassword
                    :user-id="record.id"
                    :limit="limit"
                    :offset="offset"
                />
              </div>
              <div :class="b('button')">
                <a-popconfirm title="Sure to delete?" @confirm="deleteUser(record.id)">
                  <a-button type="primary" html-type="button" danger>
                    Delete
                  </a-button>
                </a-popconfirm>
              </div>
            </div>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<style src="./style.scss" scoped lang="scss"></style>
